import {
    ApolloClient, ApolloLink, concat, createHttpLink,
    HttpLink,
    InMemoryCache,
    NormalizedCacheObject,
} from "@apollo/client";
import { GraphQLErrors } from "@apollo/client/errors";

import { onError } from "@apollo/client/link/error";
import { GraphQLError } from "graphql";

import {
    GetActiveSessionsDocument, GetActiveSessionsQuery, GetMigrationTokenDocument,
    LoginWithTokenDocument, LoginWithTokenMutationResult,
} from "./__generated__/api";
import { LoggingHandler } from "./contexts/logging/LoggingContext";

export const FOQAL_GRAPHQL_URL = process.env.REACT_APP_API_URL;
export const FQOAL_GRAPHQL_URL_NEW = process.env.REACT_APP_API_URL_NEW;

let currentUrl = FOQAL_GRAPHQL_URL;
const changeableFetch = (function newFetch(...args: unknown[]) {
    if (args[0] === FOQAL_GRAPHQL_URL) {
        // eslint-disable-next-line no-param-reassign
        args[0] = currentUrl;
    }
    return fetch(...args as Parameters<typeof fetch>);
});

function defaultDomain() {
    const wind = window as unknown as Record<string, unknown>;
    const isSecureBrowser = wind.safari != null // safari
                || (wind.navigator as Record<string, unknown>).brave // Brave Browser
                || navigator.userAgent.toLowerCase().includes("firefox"); // Firefox
    const url = isSecureBrowser ? FQOAL_GRAPHQL_URL_NEW : FOQAL_GRAPHQL_URL;
    LoggingHandler.instance.debug({ url }, "Using default domain");
    return url;
}

const DOMAIN_UPGRADE_KEY = "domain-upgrade";
function domainUpgradeCheck() {
    const currentHref = new URL(window.location.href);
    if (currentHref.searchParams.get("domain-upgrade")) {
        localStorage.setItem(DOMAIN_UPGRADE_KEY, "true");
    } else if (currentHref.searchParams.get("domain-downgrade")) {
        localStorage.removeItem(DOMAIN_UPGRADE_KEY);
    }
}
domainUpgradeCheck();

const link = createHttpLink({
    uri: localStorage.getItem(DOMAIN_UPGRADE_KEY) ? FQOAL_GRAPHQL_URL_NEW : defaultDomain(),
    credentials: "include",
    fetch: changeableFetch,
}) as HttpLink;

const WHITELISTED_PATHS = ["/login", "/answers", "/channel-invite", "/onboarding/wait-for-domain", "/actions"];

const UNAUTHORIZED_MESSAGE = "Not authorized to perform this action.";

type ErroHandler = () => void;
const authErrorRegistry: ErroHandler[] = [];

export function subscribeError(err: ErroHandler) {
    authErrorRegistry.push(err);
}

export function unsubscribeError(err: ErroHandler) {
    const index = authErrorRegistry.findIndex(err);
    authErrorRegistry.splice(index, 1);
}

export function hasLoginError(errors?: GraphQLError[] | GraphQLErrors) {
    return errors?.some(error => {
        const ext = error.extensions as {code: number, exception: {code: string}} | undefined;
        return error.message === UNAUTHORIZED_MESSAGE || ext?.code === 401 || ext?.exception?.code === "401";
    });
}

function handleLoginError(operationName: string, errors?: GraphQLError[] | GraphQLErrors) {
    const currentPath = window.location.pathname;

    if (hasLoginError(errors)
        && (WHITELISTED_PATHS.every(path => !currentPath?.startsWith(path) && currentPath !== "/"))) {
        authErrorRegistry.forEach(handler => handler());

        LoggingHandler.instance.error({
            component: "Auth",
            operationName,
        }, "Not Authenticated. Redirecting to login");

        const params = new URLSearchParams();
        params.append("redirect", window.location.href);
        window.location.href = `/login?${params.toString()}`;
    }
}

const timeStartLink = new ApolloLink((operation, forward) => {
    operation.setContext({ startTime: performance.now() });
    return forward(operation);
});

const logLink = new ApolloLink((operation, forward) => forward(operation).map(response => {
    const context = operation.getContext();

    if (operation.operationName === "StoreLogs") {
        return response;
    }
    LoggingHandler.instance.debug({
        component: "API",
        requestId: context?.response?.headers?.get("x-req-id"),
        operationName: operation.operationName,
        responseStatus: context?.response.status,
        responseTime: performance.now() - context.startTime as number,
    }, "API request");

    return response;
}));

const errorLink = onError(err => {
    const { graphQLErrors, networkError, operation } = err;
    // const has401 = graphQLErrors?.find(err => err.extensions.)
    if (graphQLErrors) {
        const context = operation.getContext();
        // eslint-disable-next-line no-console
        LoggingHandler.instance.error({
            component: "API",
            requestId: context?.response?.headers?.get("x-req-id"),
            operationName: operation.operationName,
            responseStatus: context?.response.status,
            responseTime: performance.now() - context.startTime as number,
            errors: graphQLErrors.map(graphError => ({
                message: graphError.message,
                location: graphError.locations,
                path: graphError.path,
                innerError: graphError.originalError,
            })),
        }, "GraphQL Error");

        handleLoginError(operation.operationName, graphQLErrors);
    }

    if (networkError) {
        // eslint-disable-next-line no-console
        LoggingHandler.instance.error(networkError, { component: "API" }, "Network error occured");
    }
});

const cache = new InMemoryCache();
const client = new ApolloClient({
    link: concat(timeStartLink, concat(errorLink, concat(logLink, link))),
    cache,
});

let isMigrationStarted = false;

async function isLoggedIntoDomain(testClient: ApolloClient<NormalizedCacheObject>) {
    try {
        const session = await testClient.query<GetActiveSessionsQuery>({
            query: GetActiveSessionsDocument,
        });

        return session.data.session?.id != null;
    } catch (err) {
        // continue logging in.
    }

    return false;
}
export async function upgradeClientDomain() {
    if (isMigrationStarted || currentUrl === FQOAL_GRAPHQL_URL_NEW) {
        return;
    }

    isMigrationStarted = true;

    LoggingHandler.instance.info({}, "Starting migration to new domain");

    const newClient = new ApolloClient({
        link: createHttpLink({
            uri: FQOAL_GRAPHQL_URL_NEW,
            credentials: "include",
        }),
        cache: new InMemoryCache(),
    });
    if (await isLoggedIntoDomain(newClient)) {
        currentUrl = FQOAL_GRAPHQL_URL_NEW;
        isMigrationStarted = false;
        LoggingHandler.instance.debug("Already logged into the new domain");
        return;
    }

    LoggingHandler.instance.info({}, "Creating login token and applying to new instance");
    const response = await client.mutate({
        mutation: GetMigrationTokenDocument,
    });

    const { token } = response.data.getMigrationToken as {token: string};
    await newClient.mutate<LoginWithTokenMutationResult["data"]>({
        mutation: LoginWithTokenDocument,
        variables: {
            token,
        },
    });
    LoggingHandler.instance.debug("Successfully logged into new domain");

    if (await isLoggedIntoDomain(newClient)) {
        currentUrl = FQOAL_GRAPHQL_URL_NEW;
        LoggingHandler.instance.debug("Successfully logged into new domain");
    }
    isMigrationStarted = false;
}

export default client;

export function removeTypename<T extends {__typename?: string}>(obj: T): T {
    if (obj?.__typename) {
        // eslint-disable-next-line no-param-reassign
        delete obj.__typename;
    }
    Object.values(obj).forEach(value => {
        if (value) {
            if (Array.isArray(value)) {
                value.forEach(row => {
                    if (typeof row === "object") {
                        removeTypename(row);
                    }
                });
            } else if (typeof value === "object") {
                removeTypename(value);
            }
        }
    });
    return obj;
}

export function removeTypenameList<T extends {__typename?: string}>(obj: T[]): T[] {
    return obj.map(row => removeTypename({ ...row }));
}

export type DeepComparer = (path: string[], left: unknown, right: unknown) => boolean | undefined;

export function isDeepEqual(
    left: unknown,
    right: unknown,
    path?: string[],
    comparer?: DeepComparer,
): boolean {
    if (comparer) {
        const isEqual = comparer?.(path ?? [], left, right);
        if (isEqual != null) {
            return isEqual;
        }
    }

    if (left == null && right == null) {
        return true;
    }
    // eslint-disable-next-line eqeqeq
    if ((left == null || right == null) || (typeof left !== typeof right && left != right)) {
        return false;
    }

    if (Array.isArray(left) && Array.isArray(right)) {
        return left.length === right.length && left.every((element, index) =>
            // eslint-disable-next-line implicit-arrow-linebreak
            isDeepEqual(element, right[index], path ? [...path, index.toString()] : undefined, comparer));
    }

    if (typeof left === "object" && typeof right === "object") {
        const keysA = Object.keys(left).filter(key => key !== "__typename");
        const keysB = Object.keys(right).filter(key => key !== "__typename");

        return keysA.concat(keysB)
            .dedupe()
            .every(key => isDeepEqual(
                left[key as keyof typeof left],
                right[key as keyof typeof right],
                path ? [...path, key] : undefined,
                comparer,
            ));
    }

    // Otherwise, they are equal
    return left === right;
}
